import { styled } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Input from '../../../components/Input';
import UserSvgIcon from '../../../assets/svg/general-setting/UserSvgIcon';
import { MainFeature, UserLimit } from '../../../types';
import { PLAN_LIMITS } from '../../../common/setting';

const Container = styled('div')`
  border: 1px solid #e5e5e5;
  padding: 16px;
  border-radius: 8px;
  color: black;
  & > .title {
    display: flex;
    align-items: center;
  }
  & > label {
    padding-top: 15px;
    color: var(--Secondary-60, #a1a1a1);
    font-family: 'Noto Sans';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }

  & > .container-user {
    display: flex;
    margin-bottom: 10px;

    & > .text-name {
      margin-left: 10px;
      display: flex;
      flex-direction: column;
      color: var(--Secondary-60, #a1a1a1);
      font-family: 'Noto Sans';
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
    }
  }

  & .warning-message {
    color: #e6a600;
    font-size: 0.75rem;
  }
`;

type OnChangeParams = {
  admin: number;
  supervisor: number;
  viewer: number;
};

interface UserLimitContentProps {
  userLimit: UserLimit | undefined;
  mainFeatureType: MainFeature;
  onChange: (params: OnChangeParams) => void;
}

const UserLimitContent: React.FC<UserLimitContentProps> = ({
  userLimit,
  onChange,
  mainFeatureType,
}) => {
  const [localUserLimit, setLocalUserLimit] = useState<UserLimit>({
    admin: userLimit?.admin,
    viewer: userLimit?.viewer,
    supervisor: userLimit?.supervisor,
  });

  useEffect(() => {
    setLocalUserLimit({
      admin: userLimit?.admin,
      viewer: userLimit?.viewer,
      supervisor: userLimit?.supervisor,
    });
  }, [userLimit]);

  const limits: UserLimit = PLAN_LIMITS[mainFeatureType];

  const validateAdminLimit = (value: number): string | undefined => {
    if (value < 0) {
      return '▲Warning: Admin limit cannot be negative';
    }

    if (limits?.admin && value > limits.admin) {
      return `▲Warning: Admin limit exceed ${mainFeatureType} default limit of ${limits.admin}`;
    }
  };

  const validateSupervisorLimit = (value: number): string | undefined => {
    if (value < 0) {
      return '▲Warning: Supervisor limit cannot be negative';
    }

    if (limits?.supervisor && value > limits.supervisor) {
      return `▲Warning: Supervisor limit exceed ${mainFeatureType} default limit of ${limits.supervisor}`;
    }
  };

  const validateViewerLimit = (value: number): string | undefined => {
    if (value < 0) {
      return '▲Warning: Viewer limit cannot be negative';
    }

    if (limits?.viewer && value > limits.viewer) {
      return `▲Warning: Viewer limit exceed ${mainFeatureType} default limit of ${limits.viewer}`;
    }
  };

  const adminWarningMessage = validateAdminLimit(localUserLimit.admin ?? 0);
  const supervisorWarningMessage = validateSupervisorLimit(
    localUserLimit.supervisor ?? 0,
  );
  const viewerWarningMessage = validateViewerLimit(localUserLimit.viewer ?? 0);

  return (
    <Container>
      <div className="title">
        <UserSvgIcon />
        User Limit
      </div>
      <label>
        Admin limit (max{' '}
        {isFinite(limits?.admin ?? 0) ? limits?.admin : 'unlimited'})
      </label>
      <Input
        type="number"
        sx={{ width: '100%', display: 'block' }}
        value={localUserLimit?.admin}
        min={0}
        max={limits?.admin ?? 0}
        placeholder={`Default: ${limits?.admin}`}
        onChange={(e) => {
          const value = Number((e.target as HTMLInputElement).value);

          setLocalUserLimit(() => ({ ...localUserLimit, admin: value }));
          onChange({
            admin: value,
            supervisor: localUserLimit.supervisor ?? 0,
            viewer: localUserLimit.viewer ?? 0,
          });
        }}
      />
      <div className="warning-message">{adminWarningMessage}</div>
      <label>
        Supervisor limit (max{' '}
        {isFinite(limits?.supervisor ?? 0) ? limits?.supervisor : 'unlimited'})
      </label>
      <Input
        type="number"
        sx={{ width: '100%', display: 'block' }}
        value={localUserLimit?.supervisor}
        max={limits?.supervisor ?? 0}
        placeholder={`Default: ${limits?.supervisor}`}
        onChange={(e) => {
          const value = Number((e.target as HTMLInputElement).value);

          setLocalUserLimit(() => ({
            ...localUserLimit,
            supervisor: value,
          }));
          onChange({
            admin: localUserLimit.admin ?? 0,
            supervisor: value,
            viewer: localUserLimit.viewer ?? 0,
          });
        }}
      />
      <div className="warning-message">{supervisorWarningMessage}</div>
      <label>
        Viewer limit (max{' '}
        {isFinite(limits?.viewer ?? 0) ? limits?.viewer : 'unlimited'})
      </label>
      <Input
        type="number"
        sx={{ width: '100%', display: 'block' }}
        value={localUserLimit?.viewer}
        max={limits?.viewer ?? 0}
        placeholder={`Default: ${limits?.viewer}`}
        onChange={(e) => {
          const value = Number((e.target as HTMLInputElement).value);

          setLocalUserLimit(() => ({
            ...localUserLimit,
            viewer: value,
          }));
          onChange({
            admin: localUserLimit.admin ?? 0,
            supervisor: localUserLimit.supervisor ?? 0,
            viewer: value,
          });
        }}
      />
      <div className="warning-message">{viewerWarningMessage}</div>
    </Container>
  );
};

export default UserLimitContent;
